/**
 * StarShipItSection — org-wide StarShipIt shipping integration (extracted
 * from the old god component's `renderStarShipIt`).
 *
 * Vault credentials are write-only — the backend never returns the actual
 * API key / subscription key, so "dirty" just means a non-empty key field
 * or a toggled enable switch. Test Connection reads whatever is already
 * stored in Vault (not the pending field values), so it stays an inline
 * action outside the dirty-save model.
 */
import { useEffect, useState } from 'react';
import { Truck, Loader2, Send, ShieldCheck, ShieldAlert, KeyRound, X } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SecretInput } from '@/components/ui/secret-input';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { SettingsSection } from '../components/console/SettingsSection';
import { SettingRow, SettingRowDivider } from '../components/console/SettingRow';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

export function StarShipItSection() {
  const settingsQuery = trpc.productionSettings.getStarShipItSettings.useQuery();
  const updateMutation = trpc.productionSettings.updateStarShipItSettings.useMutation();
  const testMutation = trpc.productionSettings.testStarShipIt.useMutation();

  const [apiKey, setApiKey] = useState('');
  const [subscriptionKey, setSubscriptionKey] = useState('');
  const [isEditingCredentials, setIsEditingCredentials] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (settingsQuery.data && !loaded) {
      setEnabled(settingsQuery.data.starshipitEnabled);
      setLoaded(true);
    }
  }, [settingsQuery.data, loaded]);

  const serverEnabled = settingsQuery.data?.starshipitEnabled ?? false;
  const hasCredentialDraft = apiKey !== '' || subscriptionKey !== '';
  const hasCompleteCredentialDraft = apiKey !== '' && subscriptionKey !== '';
  const isDirty = loaded && (hasCredentialDraft || enabled !== serverEnabled);

  useDirtyRegistration(
    'starshipit',
    isDirty
      ? {
          label: 'StarShipIt',
          save: async () => {
            if (hasCredentialDraft && !hasCompleteCredentialDraft) {
              toast.error('Both StarShipIt keys are required');
              throw new Error('Both StarShipIt keys are required');
            }
            await updateMutation.mutateAsync({
              starshipitApiKey: apiKey || null,
              starshipitSubscriptionKey: subscriptionKey || null,
              starshipitEnabled: enabled,
            });
            setApiKey('');
            setSubscriptionKey('');
            setIsEditingCredentials(false);
            await settingsQuery.refetch();
          },
          discard: () => {
            setApiKey('');
            setSubscriptionKey('');
            setIsEditingCredentials(false);
            setEnabled(serverEnabled);
          },
        }
      : null,
  );

  const handleTest = () => {
    testMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success('Connection successful', { description: 'StarShipIt API credentials are valid' });
        } else {
          const errorResult = result as { success: false; error: string };
          toast.error('Connection failed', { description: errorResult.error || 'Invalid credentials' });
        }
      },
      onError: (error) => toast.error('Test failed', { description: error.message }),
    });
  };

  const hasCredentials = settingsQuery.data?.hasCredentials ?? false;

  return (
    <SettingsSection
      id="starshipit"
      title="StarShipIt"
      description="Enable StarShipIt and store its API credentials."
      icon={Truck}
      scope="org"
    >
      {settingsQuery.isPending ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          <SettingRow
            htmlFor="starshipit-enabled"
            label="Enable StarShipIt"
            description={
              enabled ? (
                <>
                  Integration is <span className="font-medium text-green-600">enabled</span>. Labels will be
                  generated at packing.
                </>
              ) : (
                <>
                  Integration is <span className="font-medium text-amber-600">disabled</span>. Use manual
                  shipping.
                </>
              )
            }
          >
            <Switch
              id="starshipit-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={updateMutation.isPending}
              aria-describedby="starshipit-enabled-status"
            />
            <span id="starshipit-enabled-status" className="sr-only">
              {enabled ? 'Currently enabled' : 'Currently disabled'}
            </span>
          </SettingRow>

          <SettingRowDivider />

          {hasCredentials ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <ShieldCheck className="h-5 w-5 text-green-600 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-800">API credentials saved</p>
                <p className="text-xs text-green-700/80">Both keys are stored securely in Vault.</p>
              </div>
              {!isEditingCredentials && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2 border-green-300 bg-white/70 text-green-800 hover:bg-white"
                  onClick={() => setIsEditingCredentials(true)}
                  aria-controls="starshipit-credential-editor"
                  aria-expanded={false}
                >
                  <KeyRound className="h-4 w-4" aria-hidden="true" />
                  Replace credentials
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800">No API credentials configured</p>
                <p className="text-xs text-amber-700/80">Get both keys from StarShipIt &gt; Settings &gt; API</p>
              </div>
              {!isEditingCredentials && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2 border-amber-300 bg-white/70 text-amber-900 hover:bg-white"
                  onClick={() => setIsEditingCredentials(true)}
                  aria-controls="starshipit-credential-editor"
                  aria-expanded={false}
                >
                  <KeyRound className="h-4 w-4" aria-hidden="true" />
                  Add credentials
                </Button>
              )}
            </div>
          )}

          {isEditingCredentials && (
            <div id="starshipit-credential-editor" className="space-y-4 rounded-lg border bg-muted/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">
                    {hasCredentials ? 'Replace API credentials' : 'Add API credentials'}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Enter both keys; existing values cannot be viewed.</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-10 gap-2"
                  onClick={() => {
                    setApiKey('');
                    setSubscriptionKey('');
                    setIsEditingCredentials(false);
                  }}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  Cancel
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="starshipit-api-key" className="text-sm font-medium">
                  API Key
                </Label>
                <SecretInput
                  id="starshipit-api-key"
                  name="starshipit-api-key-replacement"
                  placeholder="Enter API key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="h-10 font-mono text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="starshipit-subscription-key" className="text-sm font-medium">
                  Subscription Key
                </Label>
                <SecretInput
                  id="starshipit-subscription-key"
                  name="starshipit-subscription-key-replacement"
                  placeholder="Enter subscription key"
                  value={subscriptionKey}
                  onChange={(e) => setSubscriptionKey(e.target.value)}
                  className="h-10 font-mono text-sm"
                />
              </div>

              {hasCredentialDraft && !hasCompleteCredentialDraft && (
                <p className="text-xs font-medium text-amber-700">Enter both keys before saving.</p>
              )}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={!hasCredentials || testMutation.isPending}
              className="h-10 min-w-[120px] gap-2"
            >
              {testMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Test Connection
            </Button>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
